import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Card } from '../../../shared/components/ui/card';
import { Button } from '../../../shared/components/ui/button';
import { useAuth } from '../../authentication/hooks/useAuth';
import { useWorkspace } from '@/features/workspace-sub-delegation';
import { useClientList } from '../../sp-client-management/hooks/useClientList';
import { useClients } from '@/features/client-relationships';
import CareConciergContactForm from './CareConciergContactForm';
import { useConciergeChooser } from '@/features/care-concierge/chooser/ConciergeChooserProvider';
import type { SearchProvidersResponse } from '../types';
import { ProviderResultCards } from './ProviderResultCards';
import { ProviderResultsPagination } from './ProviderResultsPagination';
import {
  ProviderDirectoryModals,
  type SelectedConsultationProvider,
  type SelectedProvider,
} from './ProviderDirectoryModals';
import { useCreateIntakeFormEngagement } from '../../consultations/hooks/useIntakeFormEngagement';


// Feature 104: no-results prompt with the truthful pitch, routing to the chooser.
const NoResultsConciergePrompt = () => {
  const { openConcierge } = useConciergeChooser();
  return (
    <Card className="mt-6 p-6 text-center">
      <p className="text-gray-700">
        We couldn&apos;t find a match here, but our Care Concierge team searches beyond the
        directory.
      </p>
      <Button
        onClick={() => openConcierge('directory-no-results')}
        className="mt-4 bg-[#E2A807] text-white hover:bg-[#C99006]"
      >
        Talk to a Care Concierge
      </Button>
    </Card>
  );
};

type ProviderDirectoryResultsProps = {
  searchResult: SearchProvidersResponse | null;
  isLoading: boolean;
  isError: boolean;
  hasActiveFilters: boolean;
  onPageChange: (page: number) => void;
  onRetry: () => void;
  onClearFilters: () => void;
  searchCategoryNames?: string[];
  /** Feature 104: care seekers + guests get the chooser prompt; professionals keep the FA-capable form. */
  isCareSeekerAudience?: boolean;
};

export const ProviderDirectoryResults = ({
  searchResult,
  isLoading,
  isError,
  hasActiveFilters,
  onPageChange,
  onRetry,
  onClearFilters,
  searchCategoryNames,
  isCareSeekerAudience = true,
}: ProviderDirectoryResultsProps) => {
  const { user } = useAuth();
  // Delegates act under the workspace owner's role + WORKSPACE_OPERATIONS grant.
  const { effectiveRole, hasWorkspacePermission } = useWorkspace();

  // Modal state - hoisted to parent to prevent multiple modals
  const [showSharePrompt, setShowSharePrompt] = useState(false);
  const [showReferModal, setShowReferModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<SelectedProvider | null>(null);

  // Feature 052/102: Consultation modal state
  const [callRequestModalOpen, setCallRequestModalOpen] = useState(false);
  const [intakeFormModalOpen, setIntakeFormModalOpen] = useState(false);
  const [selectedConsultationProvider, setSelectedConsultationProvider] =
    useState<SelectedConsultationProvider | null>(null);
  const createIntakeFormEngagement = useCreateIntakeFormEngagement();

  // Determine effective role (workspace owner's role for delegates).
  const role = effectiveRole ?? user?.role;
  const canOperate = hasWorkspacePermission('WORKSPACE_OPERATIONS');
  const isSP = role === 'SERVICE_PROVIDER';
  const isFA = role === 'FINANCIAL_ADVISOR';

  // Fetch clients once at parent level
  const { data: spClientsData } = useClientList(
    user?.id || '',
    { limit: 1, enabled: isSP }
  );

  const { data: faClientsData } = useClients(undefined, isFA);

  const hasClients = isSP
    ? (spClientsData?.meta?.total || 0) > 0
    : isFA
      ? (faClientsData?.data?.length || 0) > 0
      : false;

  // Handle share prompt callbacks
  const handleSharePromptOpen = (provider: SelectedProvider) => {
    // Avoid opening the share prompt while the referral modal is already open
    if (showReferModal) {
      return;
    }

    // Always replace with the latest selected provider for rapid actions
    setSelectedProvider(provider);
    setShowSharePrompt(true);
  };

  const handleConfirmShare = () => {
    setShowSharePrompt(false);
    setTimeout(() => setShowReferModal(true), 100);
  };

  const handleCancelShare = () => {
    setShowSharePrompt(false);
    setSelectedProvider(null);
  };

  const handleReferModalClose = (open: boolean) => {
    setShowReferModal(open);
    if (!open) {
      setSelectedProvider(null);
    }
  };

  // Feature 052/103/102: Handle schedule click. The Key pilot's intake form
  // (intakeFormUrl) keeps its bypass; every other provider opens the
  // Request-a-call modal (Feature 102). Cards are primary-only — Calendly
  // scheduling stays reachable from the profile page's secondary button.
  const handleScheduleClick = (provider: {
    id: string;
    name: string;
    calendlyUrl: string | null;
    intakeFormUrl: string | null;
  }) => {
    setSelectedConsultationProvider(provider);

    // Ensure mutual exclusion - close all modals before opening the correct one
    setCallRequestModalOpen(false);
    setIntakeFormModalOpen(false);
    if (provider.intakeFormUrl) {
      // Feature 103 (wayfinder ticket 18): records the billable engagement
      // at the moment of the click; the modal opens regardless of the
      // call's outcome. Search cards don't track a profile-view engagement
      // id, so there's nothing to forward for conversion here.
      createIntakeFormEngagement.mutate({ providerId: provider.id });
      setIntakeFormModalOpen(true);
    } else {
      setCallRequestModalOpen(true);
    }
  };

  // Feature 104 follow-up: the "Showing X of Y providers" count is removed —
  // the page no longer discloses the provider total. Loading text remains.
  const renderResultsCount = () => {
    if (isLoading) {
      return <p className="text-gray-600">Loading providers...</p>;
    }

    return null;
  };

  // Loading State
  if (isLoading) {
    return (
      <>
        <div className="mb-6">{renderResultsCount()}</div>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#026857]" role="status" />
          <span className="ml-3 text-gray-600">Loading providers...</span>
        </div>
      </>
    );
  }

  // Error State
  if (isError) {
    return (
      <>
        <div className="mb-6">{renderResultsCount()}</div>
        <Card className="p-8 text-center">
          <p className="text-red-600 font-medium mb-2">Error loading providers</p>
          <p className="text-gray-600">
            We encountered an error while fetching providers. Please try again.
          </p>
          <Button
            onClick={onRetry}
            className="mt-4 bg-[#026857] hover:bg-[#025247]"
          >
            Retry
          </Button>
        </Card>
      </>
    );
  }

  // Empty State
  if (!searchResult || searchResult.providers.length === 0) {
    return (
      <>
        <div className="mb-6">{renderResultsCount()}</div>
        <Card className="p-8 text-center">
          <p className="text-gray-900 font-medium mb-2">No providers found</p>
          <p className="text-gray-600 mb-4">
            {hasActiveFilters
              ? 'Try adjusting your filters to see more results'
              : 'No providers match your search criteria'}
          </p>
          {hasActiveFilters && (
            <Button
              onClick={onClearFilters}
              variant="outline"
              className="border-[#026857] text-[#026857] hover:bg-[#026857]/5"
            >
              Clear filters
            </Button>
          )}
        </Card>

        {/* Feature 104: care-seeker audience routes to the shared chooser;
            professionals keep the FA-capable contact form as before. */}
        {isCareSeekerAudience ? (
          <NoResultsConciergePrompt />
        ) : (
          <CareConciergContactForm
            pageOrigin="provider-directory-no-results"
            initialCategories={searchCategoryNames}
          />
        )}
      </>
    );
  }

  // Provider Cards
  return (
    <>
      <div className="mb-6">{renderResultsCount()}</div>

      <ProviderResultCards
        providers={searchResult.providers}
        searchContextId={searchResult.searchContextId}
        hasClients={hasClients}
        canSchedule={role === 'CARE_SEEKER' && canOperate}
        onSharePromptOpen={handleSharePromptOpen}
        onSchedule={handleScheduleClick}
      />

      <ProviderDirectoryModals
        isSP={isSP}
        isFA={isFA}
        selectedProvider={selectedProvider}
        selectedConsultationProvider={selectedConsultationProvider}
        showSharePrompt={showSharePrompt}
        setShowSharePrompt={setShowSharePrompt}
        showReferModal={showReferModal}
        onReferModalClose={handleReferModalClose}
        callRequestModalOpen={callRequestModalOpen}
        setCallRequestModalOpen={setCallRequestModalOpen}
        intakeFormModalOpen={intakeFormModalOpen}
        setIntakeFormModalOpen={setIntakeFormModalOpen}
        clearSelectedConsultationProvider={() => setSelectedConsultationProvider(null)}
        onConfirmShare={handleConfirmShare}
        onCancelShare={handleCancelShare}
      />

      <ProviderResultsPagination
        page={searchResult.page}
        totalPages={searchResult.totalPages}
        onPageChange={onPageChange}
      />
    </>
  );
};

